import { apiClient } from "./authService";
import { superadminClient } from "./superadminService";

export type CenterChannel = "PUSH" | "EMAIL" | "SMS" | "WHATSAPP";

export const CENTER_CHANNELS: CenterChannel[] = ["PUSH", "EMAIL", "SMS", "WHATSAPP"];

export type CellBlocker = "NOT_SUPPORTED" | "LOCKED" | "TEMPLATE_PENDING" | "CHANNEL_OFF";

export type NotificationCell = {
  /** The owner's choice (or the default), shown on the switch. */
  enabled: boolean;
  /** Whether the message actually goes out on this channel right now. */
  active: boolean;
  blocker: CellBlocker | null;
  isDefault: boolean;
  note: string | null;
};

export type NotificationEvent = {
  key: string;
  audience: "CLIENT" | "OWNER" | "ACCOUNT";
  label: string;
  description: string;
  locked: boolean;
  whatsappTemplate: { name: string; ready: boolean } | null;
  cells: Record<CenterChannel, NotificationCell | null>;
};

export type ChannelInfo = {
  channel: CenterChannel;
  paid: boolean;
  enabled: boolean;
  senderId: string | null;
  requestedAt: string | null;
  enabledAt: string | null;
};

export type PaidChannel = "SMS" | "WHATSAPP";

export const PAID_CHANNELS: PaidChannel[] = ["SMS", "WHATSAPP"];

/** DEFAULT: the standard price for everyone. ORGANIZATION: a price of its own. */
export type PriceSource = "DEFAULT" | "ORGANIZATION";

export type PricePeriod = {
  from: string;
  /** Null while it is the latest price. */
  to: string | null;
  /** Per delivered message. Null: no price set yet. */
  price: number | null;
  /** Superadmin only: our cost, and whether it is the default or the org's own. */
  cost?: number | null;
  source?: PriceSource | null;
};

export type ChannelPricing = {
  channel: PaidChannel;
  current: PricePeriod | null;
  upcoming: PricePeriod | null;
  /** Newest first, including a change that has not started yet. */
  history: PricePeriod[];
};

export type NotificationCenterOverview = {
  defaultSenderName: string;
  contactWhatsapp: string | null;
  channels: ChannelInfo[];
  events: NotificationEvent[];
  /** Null when prices could not be loaded. */
  pricing: { currency: string; channels: ChannelPricing[] } | null;
};

export type UsageCounts = {
  sent: number;
  failed: number;
  pending: number;
  /** Charged: the prices saved on delivered messages. */
  amount: number;
  /** What those messages cost us. Superadmin only. */
  cost?: number;
  /** Delivered SMS/WhatsApp messages sent before a price was set. */
  unpriced: number;
};

type Money = { amount: number; cost?: number };

export type NotificationUsage = Money & {
  /** False when the notification service could not be reached. */
  available: boolean;
  from: string;
  to: string;
  currency: string;
  totals: Record<CenterChannel, UsageCounts>;
  byEvent: (Money & {
    event: string;
    label: string;
    /** Counted but never charged: login codes, password resets. */
    free: boolean;
    channels: Record<CenterChannel, UsageCounts>;
  })[];
  byOrganization: (Money & {
    organizationId: string;
    organizationName: string | null;
    platform: string | null;
    channels: Record<CenterChannel, UsageCounts>;
  })[];
};

/** A price per message: at least 3 decimals, up to the 4 we store. */
export const formatUnitPrice = (value: number | null | undefined, currency = "USD") =>
  value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency,
        minimumFractionDigits: 3,
        maximumFractionDigits: 4,
      }).format(value);

/** A total: 2 decimals. */
export const formatMoney = (value: number | null | undefined, currency = "USD") =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value ?? 0);

export type UsageRange = { from: string; to: string };

/** This month so far, or the whole of last month. */
export const usageRange = (which: "this" | "last"): UsageRange => {
  const now = new Date();
  const startOfThis = new Date(now.getFullYear(), now.getMonth(), 1);
  if (which === "this") return { from: startOfThis.toISOString(), to: now.toISOString() };
  const startOfLast = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { from: startOfLast.toISOString(), to: startOfThis.toISOString() };
};

// --- Owner ---

export const getNotificationCenter = async (): Promise<NotificationCenterOverview> => {
  const { data } = await apiClient.get<NotificationCenterOverview>("/notification-center");
  return data;
};

export const updateNotificationCells = async (
  cells: { event: string; channel: CenterChannel; enabled: boolean }[]
): Promise<NotificationCenterOverview> => {
  const { data } = await apiClient.put<NotificationCenterOverview>(
    "/notification-center/settings",
    { cells },
    // The page rolls the switch back and shows its own message.
    { suppressToast: true } as any
  );
  return data;
};

export const requestNotificationChannel = async (
  channel: CenterChannel
): Promise<NotificationCenterOverview> => {
  const { data } = await apiClient.post<NotificationCenterOverview>(
    `/notification-center/channels/${channel}/request`
  );
  return data;
};

export const getNotificationUsage = async (range: UsageRange): Promise<NotificationUsage> => {
  const { data } = await apiClient.get<NotificationUsage>("/notification-center/usage", {
    params: range,
  });
  return data;
};

// --- Superadmin ---

export const getOrganizationNotificationCenter = async (
  organizationId: string
): Promise<NotificationCenterOverview> => {
  const { data } = await superadminClient.get<NotificationCenterOverview>(
    `/superadmin/organizations/${organizationId}/notification-center`
  );
  return data;
};

export const getOrganizationNotificationUsage = async (
  organizationId: string,
  range: UsageRange
): Promise<NotificationUsage> => {
  const { data } = await superadminClient.get<NotificationUsage>(
    `/superadmin/organizations/${organizationId}/notification-usage`,
    { params: range }
  );
  return data;
};

export const setOrganizationNotificationChannel = async (
  organizationId: string,
  channel: CenterChannel,
  change: { enabled?: boolean; senderId?: string | null }
): Promise<NotificationCenterOverview> => {
  const { data } = await superadminClient.patch<NotificationCenterOverview>(
    `/superadmin/organizations/${organizationId}/notification-channels/${channel}`,
    change
  );
  return data;
};

export const getAllNotificationUsage = async (range: UsageRange): Promise<NotificationUsage> => {
  const { data } = await superadminClient.get<NotificationUsage>("/superadmin/notification-usage", {
    params: range,
  });
  return data;
};

export type ChannelRequest = {
  organizationId: string;
  organizationName: string;
  channel: CenterChannel;
  requestedAt: string;
};

export const getNotificationChannelRequests = async (): Promise<ChannelRequest[]> => {
  const { data } = await superadminClient.get<ChannelRequest[]>(
    "/superadmin/notification-channel-requests"
  );
  return data;
};

// --- Superadmin: prices ---

export type PriceChange = {
  id: string;
  channel: PaidChannel;
  /** On an organization's change, null means "back to the default price". */
  price: number | null;
  cost: number | null;
  effectiveFrom: string;
  note: string | null;
  createdBy: string | null;
  createdAt: string;
  /** Has not started yet, so it can still be taken back. */
  scheduled: boolean;
};

export type PricingOverview = {
  currency: string;
  channels: ChannelPricing[];
  changes: PriceChange[];
  organizations: {
    organizationId: string;
    organizationName: string;
    platform: string | null;
    channels: {
      channel: PaidChannel;
      price: number | null;
      source: PriceSource | null;
      scheduled: { price: number | null; from: string } | null;
    }[];
  }[];
};

export type OrganizationPricing = {
  currency: string;
  channels: ChannelPricing[];
  changes: PriceChange[];
};

export const getPricingOverview = async (): Promise<PricingOverview> => {
  const { data } = await superadminClient.get<PricingOverview>("/superadmin/notification-pricing");
  return data;
};

export const setDefaultPrice = async (
  channel: PaidChannel,
  body: { price: number; cost?: number | null; effectiveFrom?: string; note?: string | null }
): Promise<PricingOverview> => {
  const { data } = await superadminClient.post<PricingOverview>(`/superadmin/notification-pricing/${channel}`, body);
  return data;
};

export const deleteDefaultPriceChange = async (id: string): Promise<PricingOverview> => {
  const { data } = await superadminClient.delete<PricingOverview>(`/superadmin/notification-pricing/changes/${id}`);
  return data;
};

export const getOrganizationPricing = async (organizationId: string): Promise<OrganizationPricing> => {
  const { data } = await superadminClient.get<OrganizationPricing>(
    `/superadmin/organizations/${organizationId}/notification-pricing`
  );
  return data;
};

export const setOrganizationPrice = async (
  organizationId: string,
  channel: PaidChannel,
  body: { price: number | null; effectiveFrom?: string; note?: string | null }
): Promise<OrganizationPricing> => {
  const { data } = await superadminClient.post<OrganizationPricing>(
    `/superadmin/organizations/${organizationId}/notification-pricing/${channel}`,
    body
  );
  return data;
};

export const deleteOrganizationPriceChange = async (
  organizationId: string,
  id: string
): Promise<OrganizationPricing> => {
  const { data } = await superadminClient.delete<OrganizationPricing>(
    `/superadmin/organizations/${organizationId}/notification-pricing/changes/${id}`
  );
  return data;
};
